/**
 * Retour à la page d'origine après connexion ou inscription.
 *
 * Une demande de connexion interrompt toujours autre chose : un produit qu'on
 * regardait, un panier qu'on validait. Renvoyer à l'accueil oblige à retrouver
 * ce qu'on avait sous les yeux, donc la destination voyage avec la navigation.
 *
 * Deux canaux, parce que les redirections n'ont pas toutes la même nature :
 * - `state.from` pour les navigations React Router (un clic sur « Se connecter ») ;
 * - `?next=` pour les redirections dures sur 401, qui rechargent la page et
 *   perdent le state de l'historique.
 */

const AUTH_PATHS = ["/login", "/register", "/restore", "/reinitialiser-mot-de-passe"];

/**
 * N'accepte qu'un chemin interne. Une URL absolue (`https://…`) ou
 * protocole-relative (`//evil.tld`) transformerait le retour de connexion en
 * redirection ouverte vers un site tiers.
 */
const isSafeInternalPath = (value) =>
  typeof value === "string" &&
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !AUTH_PATHS.some((p) => value === p || value.startsWith(`${p}?`));

/** Chemin courant complet, à passer en `state` vers /login ou /register. */
export const currentPath = (location) =>
  `${location?.pathname || "/"}${location?.search || ""}${location?.hash || ""}`;

/** `state` à joindre à un `navigate("/login")` ou `<Link to="/register">`. */
export const authRedirectState = (location) => ({ from: currentPath(location) });

/** Destination après authentification réussie. */
export const resolveAuthRedirect = (location, fallback = "/") => {
  const fromState = location?.state?.from;
  if (isSafeInternalPath(fromState)) return fromState;

  const next = new URLSearchParams(location?.search || "").get("next");
  if (isSafeInternalPath(next)) return next;

  return fallback;
};

/** URL de login pour une redirection dure (hors React Router). */
export const loginUrlWithReturn = () => {
  if (typeof window === "undefined") return "/login";
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return isSafeInternalPath(here) ? `/login?next=${encodeURIComponent(here)}` : "/login";
};
